import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { Loader2, LogOut, CalendarX2, Mail, Trash2, Inbox } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => format(d, "yyyy-MM-dd");
axios.defaults.withCredentials = true;

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [blocked, setBlocked] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("schedule");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`).then(({ data }) => setUser(data)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  const load = useCallback(async (m) => {
    try {
      const [{ data: av }, { data: bk }, { data: ct }] = await Promise.all([
        axios.get(`${API}/availability`, { params: { month: format(m, "yyyy-MM") } }),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/contacts`),
      ]);
      setBlocked(av.blocked_days);
      setBookings(bk.bookings);
      setContacts(ct.contacts);
    } catch {}
  }, []);

  useEffect(() => {
    if (user) load(month);
  }, [user, month, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      setUser(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  const toggleDay = async (d) => {
    if (!d) return;
    try {
      const { data } = await axios.post(`${API}/admin/blocked-days`, { date: fmt(d) });
      toast.success(data.blocked ? `${data.date} blocked off` : `${data.date} reopened`);
      load(month);
    } catch {
      toast.error("Couldn't update that day.");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Message removed");
    } catch {
      toast.error("Couldn't delete that message.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#8A5A44]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="bg-[#F3EFEA] rounded-3xl p-10 lg:p-12 border border-black/5 w-full max-w-md">
          <h1 className="font-serif-display text-3xl font-medium text-[#2C2A28]">
            Ministry <em className="italic text-[#8A5A44]">admin</em>
          </h1>
          <p className="text-sm text-[#5C5854] font-light mt-2">For Denise &amp; Richard only.</p>
          <div className="mt-8 space-y-6">
            <input
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              type="email"
              placeholder="Email"
              className="w-full bg-transparent border-b border-[#E6DFD5] focus:border-[#8A5A44] outline-none py-3 text-sm transition-colors duration-300"
              data-testid="admin-input-email"
            />
            <input
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              type="password"
              placeholder="Password"
              className="w-full bg-transparent border-b border-[#E6DFD5] focus:border-[#8A5A44] outline-none py-3 text-sm transition-colors duration-300"
              data-testid="admin-input-password"
            />
          </div>
          <button
            type="submit"
            disabled={loggingIn}
            className="mt-8 w-full rounded-full bg-[#8A5A44] hover:bg-[#734A38] disabled:opacity-60 text-[#FAF9F6] py-3.5 text-sm transition-colors duration-300"
            data-testid="admin-login-button"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const blockedSet = new Set(blocked);

  return (
    <div className="min-h-screen py-16 px-6 lg:px-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#2C2A28]">
            Ministry <em className="italic text-[#8A5A44]">dashboard</em>
          </h1>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-[#5C5854] hover:text-[#8A5A44] transition-colors duration-300"
            data-testid="admin-logout-button"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="mt-10 flex items-center gap-1 border-b border-[#E6DFD5]">
          <button
            onClick={() => setTab("schedule")}
            data-testid="admin-tab-schedule"
            className={`px-5 py-3 text-sm transition-colors duration-300 -mb-px border-b-2 ${
              tab === "schedule"
                ? "border-[#8A5A44] text-[#2C2A28]"
                : "border-transparent text-[#5C5854] hover:text-[#2C2A28]"
            }`}
          >
            Schedule &amp; bookings
          </button>
          <button
            onClick={() => setTab("inbox")}
            data-testid="admin-tab-inbox"
            className={`px-5 py-3 text-sm transition-colors duration-300 -mb-px border-b-2 flex items-center gap-2 ${
              tab === "inbox"
                ? "border-[#8A5A44] text-[#2C2A28]"
                : "border-transparent text-[#5C5854] hover:text-[#2C2A28]"
            }`}
          >
            <Inbox size={14} /> Messages
            {contacts.length > 0 && (
              <span
                data-testid="admin-inbox-badge"
                className="ml-1 text-[10px] font-medium bg-[#8A5A44] text-[#FAF9F6] rounded-full px-2 py-0.5"
              >
                {contacts.length}
              </span>
            )}
          </button>
        </div>

        {tab === "schedule" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10" data-testid="admin-schedule-panel">
            <div className="lg:col-span-5">
              <div className="bg-[#F3EFEA] rounded-3xl p-6 lg:p-8 border border-black/5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#8A5A44] flex items-center gap-2">
                  <CalendarX2 size={14} /> Tap a day to block or reopen it
                </p>
                <Calendar
                  mode="single"
                  selected={undefined}
                  onSelect={toggleDay}
                  onMonthChange={setMonth}
                  modifiers={{ blocked: (d) => blockedSet.has(fmt(d)) }}
                  modifiersClassNames={{ blocked: "bg-[#2C2A28] text-[#FAF9F6] hover:bg-[#2C2A28]/80 line-through" }}
                  className="w-full mt-4"
                  classNames={{
                    months: "flex flex-col",
                    table: "w-full border-collapse",
                    head_cell: "text-[#5C5854] w-10 font-normal text-xs uppercase tracking-wider",
                    cell: "p-0.5 text-center",
                    day: "h-10 w-10 rounded-full font-normal text-sm hover:bg-[#E6DFD5] transition-colors duration-200",
                    day_today: "border border-[#8A5A44]",
                    caption_label: "font-serif-display text-lg font-medium",
                  }}
                  data-testid="admin-calendar"
                />
                <p className="text-xs text-[#5C5854] mt-3">
                  Dark days are blocked — couples can't book them.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-[#F3EFEA] rounded-3xl p-8 lg:p-10 border border-black/5">
                <p className="text-xs uppercase tracking-[0.2em] text-[#8A5A44]">Upcoming conversations</p>
                {bookings.length === 0 ? (
                  <p className="text-sm text-[#5C5854] font-light mt-6" data-testid="admin-no-bookings">
                    No upcoming bookings yet.
                  </p>
                ) : (
                  <div className="mt-6 divide-y divide-[#E6DFD5]" data-testid="admin-bookings-list">
                    {bookings.map((b) => (
                      <div key={b.id} className="py-5 flex flex-wrap items-baseline gap-x-6 gap-y-1">
                        <p className="font-serif-display text-xl font-medium text-[#2C2A28] w-44">
                          {format(new Date(`${b.date}T12:00:00`), "EEE, MMM d")} · {b.slot}
                        </p>
                        <div className="flex-1 min-w-[200px]">
                          <p className="text-sm text-[#2C2A28]">{b.name}</p>
                          <p className="text-xs text-[#5C5854]">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                          {b.notes && <p className="text-xs text-[#5C5854] italic mt-1">"{b.notes}"</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "inbox" && (
          <div className="mt-10" data-testid="admin-inbox-panel">
            <div className="bg-[#F3EFEA] rounded-3xl p-8 lg:p-10 border border-black/5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs uppercase tracking-[0.2em] text-[#8A5A44] flex items-center gap-2">
                  <Mail size={14} /> Messages from the website
                </p>
                <p className="text-xs text-[#5C5854]">
                  {contacts.length} {contacts.length === 1 ? "message" : "messages"}
                </p>
              </div>

              {contacts.length === 0 ? (
                <div className="mt-10 text-center py-14" data-testid="admin-no-contacts">
                  <Inbox size={32} className="mx-auto text-[#8A5A44] opacity-40" />
                  <p className="font-serif-display text-2xl font-medium text-[#2C2A28] mt-4">Inbox is quiet</p>
                  <p className="text-sm text-[#5C5854] font-light mt-2">
                    New prayer requests and notes will appear here.
                  </p>
                </div>
              ) : (
                <div className="mt-6 divide-y divide-[#E6DFD5]" data-testid="admin-contacts-list">
                  {contacts.map((c) => {
                    const open = expanded === c.id;
                    return (
                      <div key={c.id} className="py-5" data-testid={`admin-contact-${c.id}`}>
                        <button
                          onClick={() => setExpanded(open ? null : c.id)}
                          className="w-full text-left flex items-start justify-between gap-4"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-3 flex-wrap">
                              <p className="font-serif-display text-lg font-medium text-[#2C2A28]">{c.name}</p>
                              <p className="text-xs text-[#5C5854]">
                                {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : ""}
                              </p>
                            </div>
                            <p className="text-xs text-[#5C5854] mt-0.5">
                              {c.email}{c.phone ? ` · ${c.phone}` : ""}
                            </p>
                            <p className={`text-sm text-[#2C2A28] mt-2 ${open ? "" : "line-clamp-1"}`}>
                              {c.message}
                            </p>
                          </div>
                          <span className="text-[#8A5A44] text-xs shrink-0 mt-1">{open ? "Hide" : "Read"}</span>
                        </button>

                        {open && (
                          <div className="mt-4 pl-1 flex flex-wrap gap-3">
                            <a
                              href={`mailto:${c.email}?subject=Re: your message to Wicks Marriage Ministries`}
                              className="text-xs px-4 py-2 rounded-full bg-[#8A5A44] hover:bg-[#734A38] text-[#FAF9F6] transition-colors duration-300"
                              data-testid={`admin-contact-reply-${c.id}`}
                            >
                              Reply by email
                            </a>
                            {c.phone && (
                              <a
                                href={`tel:${c.phone}`}
                                className="text-xs px-4 py-2 rounded-full border border-[#8A5A44] text-[#8A5A44] hover:bg-[#8A5A44] hover:text-[#FAF9F6] transition-colors duration-300"
                              >
                                Call
                              </a>
                            )}
                            <button
                              onClick={() => deleteContact(c.id)}
                              className="text-xs px-4 py-2 rounded-full border border-[#E6DFD5] text-[#5C5854] hover:border-[#2C2A28] hover:text-[#2C2A28] transition-colors duration-300 flex items-center gap-1.5"
                              data-testid={`admin-contact-delete-${c.id}`}
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
